import { UNREACHABLE } from '../domain/catalog'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { NotAnAssessmentError, normaliseReport } from '../domain/normalise'
import { VocabularyRegistry } from '../domain/registry'
import {
  MAX_REPORT_BYTES,
  MAX_SESSION_BYTES,
  fetchData,
  readFileJson,
  readResponseJson,
} from '../domain/limits'
import {
  QuotaExceeded,
  allMeta,
  allReports,
  clearReports,
  deleteReport,
  putReports,
  replaceAllMeta,
  type StoredReport,
} from './db'
import {
  InvalidSessionFile,
  exportSessionFile,
  parseSessionFile,
  readSessionReports,
} from './sessionFile'
import { usePersistentState, viewStateReplaced } from './viewState'
import { CATALOG_KEYS, SOURCES_KEY } from './catalogState'
import type { Report, ReportSource } from '../domain/types'

/**
 * The reports in the current session.
 *
 * Everything here survives a refresh. What is written to storage is the
 * original document and where it came from, never the normalised form: the
 * loader is expected to keep improving, and re-normalising on every visit means
 * reports saved months ago benefit from that rather than staying frozen.
 */

interface LoadFailure {
  ref: string
  reason: string
}

export interface ReportsState {
  reports: Report[]
  failures: LoadFailure[]
  busy: boolean
  /** True until the stored session has been read back. */
  restoring: boolean
  storageError: string | undefined
  registry: VocabularyRegistry
  vocabularyVersion: number
  addFiles: (files: Iterable<File>) => Promise<void>
  addSamples: () => Promise<void>
  addFromUrl: (url: string) => Promise<void>
  /**
   * @param fromSync Set by automatic sync: it never overrides a removal, and
   * only loading an address on purpose does.
   */
  addFromUrls: (urls: string[], options?: { fromSync?: boolean }) => Promise<void>
  /** True once the stored session and the list of removed reports are both read. */
  ready: boolean
  adopt: (report: Report) => Promise<void>
  remove: (id: string) => void
  /** What the last removal took out, for as long as it can be undone. */
  removed: Report[]
  undoRemove: () => void
  forgetRemoved: () => void
  /**
   * Addresses of reports the user removed. Sync skips them, or a removed run
   * would come straight back on the next scheduled sync.
   */
  dismissed: string[]
  clear: () => void
  /** @param palette The chart colours in use, which storage does not hold. */
  exportSession: (palette?: string) => Promise<void>
  importSession: (
    file: File,
    mode: 'merge' | 'replace',
    onPalette?: (palette: string) => void,
  ) => Promise<void>
  dismissFailures: () => void
  dismissStorageError: () => void
}

interface Loaded {
  report: Report
  stored: StoredReport
}

export function useReports(): ReportsState {
  const registry = useMemo(() => new VocabularyRegistry(), [])
  const [reports, setReports] = useState<Report[]>([])
  const [failures, setFailures] = useState<LoadFailure[]>([])
  const [busy, setBusy] = useState(false)
  const [restoring, setRestoring] = useState(true)
  const [storageError, setStorageError] = useState<string | undefined>()
  const [vocabularyVersion, setVocabularyVersion] = useState(0)
  // What pages receive: a new identity whenever resolved labels arrive.
  const currentRegistry = useMemo(
    () => (vocabularyVersion === 0 ? registry : registry.renewed()),
    [registry, vocabularyVersion],
  )
  const pending = useRef(0)
  const known = useRef(new Set<string>())
  // Read by remove(), which needs the report it is removing, not just its id.
  const latestReports = useRef(reports)
  useEffect(() => {
    latestReports.current = reports
  }, [reports])
  const [removed, setRemoved] = useState<Report[]>([])
  const [dismissed, setDismissed, dismissedRestored] = usePersistentState<string[]>(
    'dismissed-reports',
    [],
    {
      validate: (v) => Array.isArray(v) && v.every((u) => typeof u === 'string'),
    },
  )
  const undismiss = useCallback(
    (urls: string[]) => setDismissed((list) => list.filter((url) => !urls.includes(url))),
    [setDismissed],
  )

  const resolveLabels = useCallback(
    (incoming: Report[]) => {
      const uris = new Set<string>()
      for (const report of incoming) {
        for (const check of report.checks) if (check.indicatorUri) uris.add(check.indicatorUri)
      }
      if (uris.size === 0) return
      // Labels are an enhancement. Nothing waits on the network for them.
      void registry.resolve(uris).then(() => setVocabularyVersion((v) => v + 1))
    },
    [registry],
  )

  // Definitions that could not be fetched while offline are tried again once
  // the browser is back online, rather than staying unresolved all visit.
  useEffect(() => {
    const retry = () => {
      registry.retryFailed()
      resolveLabels(latestReports.current)
    }
    window.addEventListener('online', retry)
    return () => window.removeEventListener('online', retry)
  }, [registry, resolveLabels])

  // Restore whatever was here last time, before anything else happens.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      let stored: StoredReport[] = []
      try {
        stored = await allReports()
      } catch {
        setStorageError(
          'Your saved session could not be read from this browser. It has not been deleted; reloading may help.',
        )
      }
      if (cancelled) return

      const restored: Report[] = []
      const unreadable: LoadFailure[] = []
      for (const entry of stored) {
        try {
          restored.push(normaliseReport(entry.raw, entry.source))
        } catch {
          // Skipped, not deleted: the original document is kept precisely so a
          // later version that reads it can bring it back.
          unreadable.push({
            ref: entry.source?.ref ?? entry.id,
            reason: 'Saved in this browser, but this version cannot read it. It has been kept.',
          })
        }
      }
      if (unreadable.length > 0) setFailures((current) => [...current, ...unreadable])

      // Merged with anything loaded while this was being read, rather than
      // replacing it: a file dropped during the restore would otherwise vanish.
      const restoredIds = new Set(restored.map((report) => report.id))
      for (const id of restoredIds) known.current.add(id)
      setReports((current) => [...restored, ...current.filter((r) => !restoredIds.has(r.id))])
      setRestoring(false)
      resolveLabels(restored)
    })()
    return () => {
      cancelled = true
    }
  }, [resolveLabels])

  /** Adds what is new to the session and storage; false if storing failed. */
  const ingest = useCallback(
    async (incoming: Loaded[], failed: LoadFailure[]): Promise<boolean> => {
      if (failed.length > 0) setFailures((current) => [...current, ...failed])
      if (incoming.length === 0) return true

      // Which reports are new is decided here, not inside the state updater.
      // An updater does not run when it is called, it runs when React gets
      // round to it, so anything read from it on the next line is still empty.
      // Getting that wrong meant reports rendered but were never written to
      // storage, and only showed up as everything vanishing on refresh.
      const accepted = incoming.filter((entry) => !known.current.has(entry.report.id))
      if (accepted.length === 0) return true

      for (const entry of accepted) known.current.add(entry.report.id)
      setReports((current) => [...current, ...accepted.map((e) => e.report)])
      resolveLabels(accepted.map((e) => e.report))

      try {
        await putReports(accepted.map((e) => e.stored))
        return true
      } catch (error) {
        setStorageError(
          error instanceof QuotaExceeded
            ? 'These reports are loaded but could not be saved: the browser will not store any more data for this site. Remove some reports, or export this session to a file.'
            : 'These reports are loaded but could not be saved to this browser.',
        )
        return false
      }
    },
    [resolveLabels],
  )

  // Counted, so one task finishing does not clear busy while another runs.
  const withBusy = useCallback(async (task: () => Promise<void>) => {
    pending.current += 1
    setBusy(true)
    try {
      await task()
    } finally {
      pending.current -= 1
      if (pending.current === 0) setBusy(false)
    }
  }, [])

  const run = useCallback(
    async (task: () => Promise<{ loaded: Loaded[]; failed: LoadFailure[] }>) =>
      withBusy(async () => {
        const { loaded, failed } = await task()
        await ingest(loaded, failed)
      }),
    // oxlint-disable-next-line react/memo-dependencies -- ingest is called above; a false positive
    [withBusy, ingest],
  )

  const addFiles = useCallback(
    async (files: Iterable<File>) =>
      run(async () => {
        const loaded: Loaded[] = []
        const failed: LoadFailure[] = []
        for (const file of files) {
          try {
            loaded.push(
              load(await readFileJson(file, MAX_REPORT_BYTES), {
                kind: 'file',
                ref: file.name,
                loadedAt: new Date().toISOString(),
              }),
            )
          } catch (error) {
            failed.push({ ref: file.name, reason: explain(error) })
          }
        }
        return { loaded, failed }
      }),
    [run],
  )

  // Loading an address on purpose overrides having removed it before.
  const addFromUrl = useCallback(
    async (url: string) => {
      undismiss([url])
      return run(() => fetchOne(url, 'url'))
    },
    [run, undismiss],
  )

  /**
   * Several at once, in parallel. Loading a project's whole history is one
   * action from the user's point of view, so it reads as one here too.
   */
  const addFromUrls = useCallback(
    async (urls: string[], options?: { fromSync?: boolean }) => {
      if (!options?.fromSync) undismiss(urls)
      return run(() => fetchAll(urls.map((url) => [url, 'catalog'])))
    },
    [run, undismiss],
  )

  /**
   * Takes in a report that has already been read elsewhere.
   *
   * Used when a link opens one assessment directly: it has been fetched and
   * normalised by the view that resolved the link, and only needs to join the
   * session. The original document travels on the report, so what gets stored
   * is still the document rather than the interpretation of it.
   */
  const adopt = useCallback(
    async (report: Report) =>
      run(async () => ({
        loaded: [loadedFrom(report)],
        failed: [],
      })),
    [run],
  )

  // The bundled samples, with the same status and size checks as any fetch.
  const addSamples = useCallback(
    async () =>
      run(async () => {
        const base = `${import.meta.env.BASE_URL}samples/`
        try {
          const response = await fetchData(`${base}index.json`)
          if (!response.ok) throw new Error(`The server answered ${response.status}.`)
          const manifest = (await readResponseJson(response, MAX_REPORT_BYTES)) as {
            reports: { file: string }[]
          }
          return await fetchAll(
            manifest.reports.map((entry) => [`${base}${entry.file}`, 'bundled', entry.file]),
          )
        } catch (error) {
          return {
            loaded: [] as Loaded[],
            failed: [{ ref: 'sample reports', reason: explainFetch(error) }],
          }
        }
      }),
    [run],
  )

  const remove = useCallback(
    (id: string) => {
      const target = latestReports.current.find((r) => r.id === id)
      known.current.delete(id)
      setReports((list) => list.filter((r) => r.id !== id))
      void deleteReport(id)
      if (!target) return
      setRemoved([target])
      const { kind, ref } = target.source
      if (kind === 'url' || kind === 'catalog') {
        setDismissed((list) => (list.includes(ref) ? list : [...list, ref]))
      }
    },
    [setDismissed],
  )

  const undoRemove = useCallback(() => {
    const back = removed
    setRemoved([])
    undismiss(back.map((r) => r.source.ref))
    void ingest(back.map(loadedFrom), [])
  }, [removed, undismiss, ingest])

  const clear = useCallback(() => {
    known.current.clear()
    setReports([])
    setRemoved([])
    setFailures([])
    void clearReports()
  }, [])

  const exportSession = useCallback(async (palette?: string) => {
    // Read from storage rather than from state: what gets written out is the
    // original documents, which state does not hold. The view state comes from
    // the same place and for the same reason, and is taken wholesale so that a
    // preference added later needs no change here to travel with the session.
    try {
      await exportSessionFile(palette)
    } catch {
      setStorageError(
        "The session could not be exported: this browser's storage could not be read.",
      )
    }
  }, [])

  const importSession = useCallback(
    async (file: File, mode: 'merge' | 'replace', onPalette?: (palette: string) => void) =>
      withBusy(async () => {
        try {
          const parsed = parseSessionFile(await readFileJson(file, MAX_SESSION_BYTES))

          const { restored, failed } = readSessionReports(parsed)

          // Replacing with nothing would only destroy what is here.
          if (mode === 'replace' && restored.length === 0) {
            throw new InvalidSessionFile(
              'Nothing in this session file could be read, so the current session was left as it was.',
            )
          }

          // New reports are stored before old ones go, so a failure to store
          // (a full quota, say) leaves the current session intact rather than
          // leaving nothing.
          const incomingIds = new Set(restored.map((entry) => entry.report.id))
          const previous = [...known.current].filter((id) => !incomingIds.has(id))
          const saved = await ingest(restored, failed)
          if (mode === 'replace' && saved) {
            for (const id of previous) {
              known.current.delete(id)
              void deleteReport(id)
            }
            setReports((list) => list.filter((r) => incomingIds.has(r.id)))
          }

          /*
           * The state the session was read in, put back before anything renders
           * it. Filters, grouping, the page you were on and each page's own
           * selections all live in the meta store, so restoring them is one
           * write followed by a nudge to the hooks that read it. The store is
           * replaced, not added to, so what the file lacks goes back to its
           * default rather than mixing two sessions.
           *
           * Only on replace. Merging two sessions means keeping the filters you
           * are working with rather than adopting a colleague's.
           */
          if (mode === 'replace' && saved) {
            // The sources you follow stay yours: following one fetches from it on
            // a schedule, which a file someone sent should not set up unseen.
            const view = { ...parsed.view }
            const offered = view[SOURCES_KEY]
            const current = await allMeta()
            for (const key of CATALOG_KEYS) {
              if (Object.hasOwn(current, key)) view[key] = current[key]
              else delete view[key]
            }
            await replaceAllMeta(view)
            viewStateReplaced()
            if (parsed.palette) onPalette?.(parsed.palette)
            if (Array.isArray(offered) && offered.length > 0) {
              setFailures((list) => [
                ...list,
                {
                  ref: file.name,
                  reason: `The sources this session follows (${offered.length}) were not added. Add any you want on the Sources page.`,
                },
              ])
            }
          }
        } catch (error) {
          setFailures((current) => [...current, { ref: file.name, reason: explain(error) }])
        }
      }),
    [withBusy, ingest],
  )

  return {
    reports,
    failures,
    busy,
    restoring,
    storageError,
    registry: currentRegistry,
    vocabularyVersion,
    addFiles,
    addSamples,
    addFromUrl,
    addFromUrls,
    adopt,
    remove,
    ready: !restoring && dismissedRestored,
    removed,
    undoRemove,
    forgetRemoved: useCallback(() => setRemoved([]), []),
    dismissed,
    clear,
    exportSession,
    importSession,
    dismissFailures: useCallback(() => setFailures([]), []),
    dismissStorageError: useCallback(() => setStorageError(undefined), []),
  }
}

/** Reads a document into a report, with the record that stores it. */
function load(raw: unknown, source: ReportSource): Loaded {
  return loadedFrom(normaliseReport(raw, source))
}

/** `ref` names the report in the session; the address unless given. */
async function fetchOne(
  url: string,
  kind: ReportSource['kind'],
  ref = url,
): Promise<{ loaded: Loaded[]; failed: LoadFailure[] }> {
  try {
    const response = await fetchData(url)
    if (!response.ok) {
      return { loaded: [], failed: [{ ref, reason: `The server answered ${response.status}.` }] }
    }
    const raw = await readResponseJson(response, MAX_REPORT_BYTES)
    return { loaded: [load(raw, { kind, ref, loadedAt: new Date().toISOString() })], failed: [] }
  } catch (error) {
    return { loaded: [], failed: [{ ref, reason: explainFetch(error) }] }
  }
}

/** Fetches several at once, but no more than a browser handles well in parallel. */
const FETCHES_AT_ONCE = 6

async function fetchAll(
  requests: [url: string, kind: ReportSource['kind'], ref?: string][],
): Promise<{ loaded: Loaded[]; failed: LoadFailure[] }> {
  const settled: { loaded: Loaded[]; failed: LoadFailure[] }[] = []
  let next = 0
  const worker = async () => {
    while (next < requests.length) {
      const index = next++
      settled[index] = await fetchOne(...requests[index]!)
    }
  }
  await Promise.all(Array.from({ length: Math.min(FETCHES_AT_ONCE, requests.length) }, worker))
  return { loaded: settled.flatMap((r) => r.loaded), failed: settled.flatMap((r) => r.failed) }
}

/** A report already read, with the record that stores its original document. */
function loadedFrom(report: Report): Loaded {
  return {
    report,
    stored: { id: report.id, raw: report.raw, source: report.source, addedAt: Date.now() },
  }
}

function explain(error: unknown): string {
  if (error instanceof NotAnAssessmentError) {
    return 'This is valid JSON but not a software quality assessment.'
  }
  if (error instanceof SyntaxError) return 'This file is not valid JSON.'
  return error instanceof Error ? error.message : 'The file could not be read.'
}

function explainFetch(error: unknown): string {
  return error instanceof TypeError ? UNREACHABLE : explain(error)
}
